import { X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CONTROL_ICON_SIZE,
  controlShellClass,
  type ControlSize,
} from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* The text input.
 *
 * The box is the SHELL, not the `<input>`: the border, the height and the focus
 * ring live on the wrapper, and the inner element is stripped to a transparent
 * field. That is what lets an icon, a unit suffix and a clear button sit inside
 * the same box and share one focus ring, instead of each adornment being
 * absolutely positioned over a control that does not know it is there.
 *
 * `forwardRef` is required, not decorative: LoginPage registers this control
 * with react-hook-form via `{...register('email')}`, and RHF needs the ref to
 * focus the first invalid field on a failed submit.
 */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  iconLeft?: LucideIcon
  inputSize?: ControlSize
  /** A trailing static affordance — a unit, a currency code, a reveal button. */
  suffix?: ReactNode
  /** Shows a clear button once there is something to clear. Controlled inputs
   *  only: the parent owns the value, so it must own the clearing too. */
  onClear?: () => void
  /** Applied to the wrapper. The inner input is styled by the component. */
  className?: string
}

/* Base UI (`@base-ui/react`): `Input` considered and rejected. Outside a `Field.Root` it adds no
   behaviour over a native <input> — no roles, no keyboard handling — so
   adopting it would be an import and nothing else. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    iconLeft: Icon,
    inputSize = 'md',
    suffix,
    onClear,
    disabled,
    value,
    ...props
  },
  ref,
) {
  const { t } = useTranslation()

  // Only offer to clear what is actually there, and never on a disabled field.
  const clearable =
    Boolean(onClear) && !disabled && value !== undefined && String(value) !== ''

  return (
    <div
      className={cn(
        controlShellClass(inputSize),
        // Named group so the leading icon can wake up with the field. Scoped to
        // avoid capturing any outer `group` a call site may already live in.
        'group/control flex items-center gap-2',
        Icon ? 'pl-2.5' : 'pl-3',
        suffix || clearable ? 'pr-1.5' : 'pr-3',
        disabled && 'bg-surface-disabled cursor-not-allowed',
        className,
      )}
      // The wrapper carries the invalid border; the inner input carries the
      // aria-invalid a screen reader reads. Both come from the same prop.
      aria-invalid={props['aria-invalid']}
    >
      {Icon ? (
        <Icon
          size={CONTROL_ICON_SIZE}
          aria-hidden
          className={cn(
            'text-text-faint pointer-events-none shrink-0',
            // The icon wakes with the field: brand while focused, a step darker
            // under hover, faint at rest. A second, quieter cue alongside the
            // border/ring — never the only one.
            'transition-colors duration-[var(--dur-fast)]',
            'group-hover/control:text-text-muted group-focus-within/control:text-brand',
          )}
        />
      ) : null}

      <input
        ref={ref}
        disabled={disabled}
        value={value}
        className={cn(
          // Every visual property is on the shell, so the field itself
          // contributes nothing but the text and the caret. That includes the
          // global :focus-visible halo — the shell draws it via focus-within,
          // and the inner element repeating it put a second ring INSIDE the box.
          'min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-[13px]',
          'font-normal text-inherit outline-none focus-visible:shadow-none',
          'disabled:cursor-not-allowed',
        )}
        {...props}
      />

      {clearable ? (
        <button
          type="button"
          // Not in the tab order: the field it clears is one Shift+Tab away and
          // a keyboard user clears with a selection and Delete. Putting a button
          // between every input and the next field would double the tab count of
          // every form in the product.
          tabIndex={-1}
          aria-label={t('common.form.clear')}
          onClick={onClear}
          className="text-text-faint hover:text-text-body flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-[5px] transition-colors duration-[var(--dur-fast)]"
        >
          <X size={13} aria-hidden />
        </button>
      ) : null}

      {suffix ? (
        <span className="text-text-muted shrink-0 text-[12px] font-light">
          {suffix}
        </span>
      ) : null}
    </div>
  )
})
